import moment from 'moment-timezone';
import { CostChangeLookbackMode } from '@domain/configuration/types';
import { DEFAULT_CURRENCY_CODE } from '@/utils/money';
import { useGeneratedGetBusinessOrganizationPricingConfigQuery } from '@/experiences/sales-portal/blocs/documents/business-organization-pricing-config.generated';
import { useIsValidPricingLayout } from '@/experiences/sales-portal/domain/pricing/blocs/hooks/use-is-valid-pricing-layout';
import { useMemo } from 'react';

// Org-wide source of truth for pricing access control
export const useBusinessOrganizationPricingConfig = ({
  skip = false,
}: { skip?: boolean } = {}) => {
  const { data, loading, error } =
    useGeneratedGetBusinessOrganizationPricingConfigQuery({
      fetchPolicy: 'cache-first',
      // This action requires the caller to be a supplier employee in the org
      // (backend `verify_business_organization_user`). Skip for users without a
      // supplier role to avoid a guaranteed 403 ApolloError.
      skip,
    });
  // Pricing has no native phone layout, so it's excluded from access itself.
  const isValidPricingLayout = useIsValidPricingLayout();

  return useMemo(() => {
    const config = data?.getBusinessOrganizationPricingConfig;
    return {
      costChangeLookbackMode: config?.cost_change_lookback_mode as
        | CostChangeLookbackMode
        | undefined,
      costChangeWeekAnchorDay: config?.cost_change_week_anchor_day ?? undefined,
      currencyCode: config?.currency_code ?? DEFAULT_CURRENCY_CODE,
      error,
      hasBookmarkingAccess: config?.bookmarking_enabled ?? false,
      hasCustomerTableAccess: config?.customer_table_enabled ?? false,
      hasPricingAccess:
        (config?.pricing_management_enabled ?? false) && isValidPricingLayout,
      hasPricingAgentAccess: config?.pricing_agent_enabled ?? false,
      isHistoricalPricingContextEnabled:
        config?.show_invoice_history_popover ?? false,
      isPeerRangeEnabled: config?.peer_range_column_enabled ?? false,
      loading,
      timezone: config?.timezone ?? moment.tz.guess(),
    };
  }, [data, loading, error, isValidPricingLayout]);
};
